/* eslint-disable max-lines-per-function, max-lines */
import React, { ChangeEvent, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import {
  Grid,
  Typography,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";
import { FormikConfig } from "formik";
import logger from "services/logger";
import { userLoginToken } from "utils/general-utils";
import { UserRole } from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import { Viewer } from "components/auth/types";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { ServerError } from "components/graphql-views";
import { Button } from "components/haven-design/buttons";
import { Card } from "components/haven-design/card";
import { CCEmailForm } from "../property/lists/cc-email-form";
import { SettingsHeader } from "./settings-header";

type Props = {
  viewer: Viewer;
}

type UpdateRes = {
  id: string;
  email: string;
}

type UpdateVars = {
  id: string;
  data: { bccEmail: string };
}

type QueryRes = {
  viewer: Viewer;
}

const ORG_QUERY = gql`
  query orgQuery {
    viewer {
      id
      organization {
        id
        bccEmail
        logoFilename
        whoCanAssign
        pmVisibility
      }
    }
  }
`;

const UPDATE_BCC_EMAIL = gql`
  mutation updateOrgDashboard($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
      bccEmail
    }
  }
`;

const BccSettings = (props: { viewer: Viewer; data?: QueryRes; loading?: boolean }) => {
  const isLoading = !!props.loading;
  const defaultEmail = props.data?.viewer.organization?.bccEmail || "";
  const { organizationId } = props.viewer;
  if (!organizationId && !isLoading) return null;
  const { openSnackbar } = useSnackbar();

  const [updateBccEmail] = useMutation<UpdateRes, UpdateVars>(UPDATE_BCC_EMAIL);

  const onSubmit: FormikConfig<{email: string}>["onSubmit"] = (values) => {
    updateBccEmail({
      variables: { id: (organizationId as string), data: { bccEmail: (values.email || "").trim() } },
    })
      .then(() => {
        openSnackbar({ message: "Org Email saved", status: "success" });
      })
      .catch((err) => {
        openSnackbar({ message: "Error saving Organization Email", status: "error" });
        logger.sendError(err);
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2">Set automatic Bcc email</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        {isLoading ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 8, sm: 9, md: 10 }}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Skeleton variant="rectangular" height={36} width={120} />
              </div>
            </Grid>
          </Grid>
        ) : (
          <CCEmailForm initialEmail={defaultEmail} onSubmit={onSubmit} type="organization" />
        )}
      </Grid>
    </Grid>
  );
};

// who_can_assign / pm_visibility are exposed by the org GraphQL object type but
// are not yet on the generated `getViewer_viewer_organization` type, so we read
// them through this narrow shape. Values are byte-identical to the server enums
// (organization.ex): who_can_assign ["everyone","supervisors_only"],
// pm_visibility ["all","only_assigned"].
type OwnerAssignmentOrg = {
  whoCanAssign: string | null;
  pmVisibility: string | null;
};

const WHO_CAN_ASSIGN_DEFAULT = "everyone";
const PM_VISIBILITY_DEFAULT = "all";

type UpdateOwnerAssignmentRes = {
  id: string;
  whoCanAssign: string;
  pmVisibility: string;
};

type UpdateOwnerAssignmentVars = {
  id: string;
  data: { whoCanAssign?: string; pmVisibility?: string };
};

const UPDATE_OWNER_ASSIGNMENT = gql`
  mutation updateOrgOwnerAssignment($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
      whoCanAssign
      pmVisibility
    }
  }
`;

// eslint-disable-next-line max-lines-per-function
const OwnerAssignmentSettings = (props: {
  viewer: Viewer;
  data?: QueryRes;
  loading?: boolean;
}) => {
  const isLoading = !!props.loading;
  const { organizationId } = props.viewer;
  const { openSnackbar } = useSnackbar();

  const [updateOwnerAssignment] = useMutation<
  UpdateOwnerAssignmentRes,
  UpdateOwnerAssignmentVars
  >(UPDATE_OWNER_ASSIGNMENT);

  if (!organizationId && !isLoading) return null;

  const org = props.data?.viewer.organization as unknown as OwnerAssignmentOrg | undefined;
  const whoCanAssign = org?.whoCanAssign || WHO_CAN_ASSIGN_DEFAULT;
  const pmVisibility = org?.pmVisibility || PM_VISIBILITY_DEFAULT;

  const save = (data: UpdateOwnerAssignmentVars["data"], errorLabel: string) => {
    updateOwnerAssignment({
      variables: { id: organizationId as string, data },
    })
      .then(() => {
        openSnackbar({ message: "Owner Assignment settings saved", status: "success" });
      })
      .catch((err) => {
        openSnackbar({ message: errorLabel, status: "error" });
        logger.sendError(err);
      });
  };

  const onWhoCanAssignChange = (event: SelectChangeEvent) => {
    save({ whoCanAssign: event.target.value }, "Error saving who can assign owners");
  };

  const onPmVisibilityChange = (event: SelectChangeEvent) => {
    save({ pmVisibility: event.target.value }, "Error saving property manager visibility");
  };

  return (
    <Card
      data-testid="owner-assignment-section"
      style={{ marginTop: 24 }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2">Owner Assignment</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={56} />
          ) : (
            <FormControl fullWidth>
              <InputLabel id="who-can-assign-label">Who can assign owners</InputLabel>
              <Select
                labelId="who-can-assign-label"
                id="who-can-assign-select"
                label="Who can assign owners"
                value={whoCanAssign}
                onChange={onWhoCanAssignChange}
              >
                <MenuItem value="everyone">Everyone</MenuItem>
                <MenuItem value="supervisors_only">Supervisors only</MenuItem>
              </Select>
              <FormHelperText>
              Controls which users are allowed to assign owners to applications.
              </FormHelperText>
            </FormControl>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={56} />
          ) : (
            <FormControl fullWidth>
              <InputLabel id="pm-visibility-label">Property manager visibility</InputLabel>
              <Select
                labelId="pm-visibility-label"
                id="pm-visibility-select"
                label="Property manager visibility"
                value={pmVisibility}
                onChange={onPmVisibilityChange}
              >
                <MenuItem value="all">All applications</MenuItem>
                <MenuItem value="only_assigned">Only assigned applications</MenuItem>
              </Select>
              <FormHelperText>
              Controls whether property managers see all applications or only the
              applications assigned to them.
              </FormHelperText>
            </FormControl>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

type UpdateLogoRes = {
  id: string;
  logoFilename: string;
}

type UpdateLogoVars = {
  id: string;
  data: { logoFilename: string };
}

const UPDATE_LOGO = gql`
  mutation updateOrgLogo($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
    }
  }
`;

// eslint-disable-next-line max-lines-per-function
const OrgLogoSettings = (props: { data?: QueryRes; onUpload: () => void; loading?: boolean }) => {
  const [uploading, setUploading] = useState<boolean>(false);

  const [updateLogo] = useMutation<UpdateLogoRes, UpdateLogoVars>(UPDATE_LOGO);

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const token = userLoginToken();
      const formData = new FormData();
      formData.append("logo", file as Blob);
      setUploading(true);
      fetch("/uploads/upload-org-logo", {
        body: formData,
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
      })
        .then(() => {
          setUploading(false);
          props.onUpload();
        })
        .catch((err) => {
          setUploading(false);
          logger.sendError(err);
        });
    }
  };

  const deleteHandler = () => {
    if (
      // eslint-disable-next-line no-alert
      !confirm("Are you sure you want to delete your current logo?. This action can not be undone.")
    ) {
      return;
    }
    updateLogo({
      variables: {
        id: props?.data?.viewer?.organization?.id || "",
        data: { logoFilename: "" },
      },
    }).then(() => props.onUpload());
  };

  const isLoading = !!props.loading;
  const logoPath = props.data?.viewer?.organization?.logoFilename;

  return (
    <div>
      <h4>Logo</h4>
      <p>Recommended size: 150px x 150px</p>
      <div>
        {isLoading ? (
          <Skeleton variant="rectangular" width={150} height={150} />
        ) : (
          <>
            {logoPath && <img src={logoPath} width="150px" height="150px" />}
            {!logoPath && <p>No logo available</p>}
          </>
        )}
      </div>
      <div style={{ marginTop: 5 }}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={32} width={200} />
        ) : (
          <>
            <input type="file" name="org-logo" onChange={changeHandler} disabled={uploading} />
            {logoPath && (
              <Button
                style={{ marginTop: 5 }}
                color="secondary"
                onClick={deleteHandler}
                disabled={uploading}
              >
                Delete
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const OrgSettingsPage: React.FC<Props> = (props: Props) => {
  const { loading, data, error, refetch } = useQuery<QueryRes>(ORG_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  if (error) return <ServerError desc={error.message} />;
  if (!loading && !data) return <ServerError desc="Missing Data" />;

  return (
    <div>
      <div>
        <SettingsHeader title="Organization Settings" />
        <BccSettings viewer={props.viewer} data={data} loading={loading} />
        <OrgLogoSettings data={data} onUpload={refetch} loading={loading} />
        <OwnerAssignmentSettings viewer={props.viewer} data={data} loading={loading} />
      </div>
    </div>
  );
};

// Exposed for tests so MockedProvider uses the exact same gql documents as the
// component (avoids query-string mismatches against the mock registry).
export const testExports = { ORG_QUERY, UPDATE_OWNER_ASSIGNMENT, OrgSettingsPage };

export default authorizeForRole([UserRole.SUPERVISOR], OrgSettingsPage);
